import * as React from 'react'
import type { ToolPlacement } from '@/types/tool-taxonomy'

const STORAGE_KEY = 'tools-ui-placements-v1'

function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `placement_${Date.now()}_${Math.random().toString(16).slice(2)}`
}

function loadPlacements(): ToolPlacement[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function savePlacements(placements: ToolPlacement[]) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(placements))
}

export function useToolPlacements() {
  const [placements, setPlacements] = React.useState<ToolPlacement[]>(loadPlacements)
  const [loading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const refetch = React.useCallback(async () => {
    setError(null)
    setPlacements(loadPlacements())
  }, [])

  const upsertPlacement = (placement: ToolPlacement) => {
    setPlacements((prev) => {
      const idx = prev.findIndex((entry) => entry.tool_id === placement.tool_id)
      if (idx === -1) {
        const next = [...prev, placement]
        savePlacements(next)
        return next
      }
      const next = [...prev]
      next[idx] = placement
      savePlacements(next)
      return next
    })
  }

  const moveTool = async (toolId: string, folderId: string | null) => {
    const now = new Date().toISOString()
    const placement: ToolPlacement = {
      id: generateId(),
      tool_id: toolId,
      folder_id: folderId,
      created_at: now,
      updated_at: now,
    }
    upsertPlacement(placement)
    return placement
  }

  const bulkMoveTools = async (toolIds: string[], folderId: string | null) => {
    const now = new Date().toISOString()
    setPlacements((prev) => {
      const byTool = new Map(prev.map((placement) => [placement.tool_id, placement]))
      toolIds.forEach((toolId) => {
        byTool.set(toolId, {
          id: byTool.get(toolId)?.id || generateId(),
          tool_id: toolId,
          folder_id: folderId,
          created_at: byTool.get(toolId)?.created_at || now,
          updated_at: now,
        })
      })
      const next = Array.from(byTool.values())
      savePlacements(next)
      return next
    })
  }

  const prunePlacementsForTools = React.useCallback((toolIds: string[]) => {
    const allowed = new Set(toolIds)
    setPlacements((prev) => {
      const next = prev.filter((placement) => allowed.has(placement.tool_id))
      if (next.length !== prev.length) {
        savePlacements(next)
      }
      return next
    })
  }, [])

  return {
    placements,
    loading,
    error,
    refetch,
    moveTool,
    bulkMoveTools,
    prunePlacementsForTools,
  }
}
